"use client";

import { TaskStatus } from "@/types";
import { getMaxPoints } from "@/lib/challenges";
import {
  useGetCampaign,
  useGetCampaignUser,
  useGetCampaignTaskStatus,
  useQuestsMutations,
} from "@/hooks/challenges";
import { CAMPAIGN_ID, CAMPAIGN_GROUP_ID } from "@/config/campaign";
import { useAuth } from "@/hooks/useAuth";
import { QuestLogo } from "@/components";
import { images } from "@/config/images";
import { X } from "lucide-react";
import { QuestRenderer } from "./tasks/quest-renderer";
import { MarkdownRenderer } from "@/components/markdown-renderer";
import { useEffect, useRef, useCallback } from "react";

interface QuestModalProps {
  isOpen: boolean;
  onClose: () => void;
  task: TaskStatus | null;
}

export const QuestModal = ({ isOpen, onClose, task }: QuestModalProps) => {
  const { address } = useAuth();
  const { data: campaign } = useGetCampaign(CAMPAIGN_ID, CAMPAIGN_GROUP_ID);
  const { data: challengesUser } = useGetCampaignUser(CAMPAIGN_ID, CAMPAIGN_GROUP_ID);
  const { taskMutation } = useQuestsMutations();

  // Get the latest task status from the fetched data
  const { data: taskStatuses } = useGetCampaignTaskStatus(
    CAMPAIGN_ID,
    CAMPAIGN_GROUP_ID
  );

  // Use the latest task status if available, otherwise use the prop
  const currentTask =
    taskStatuses?.tasksStatus?.find(
      (t: TaskStatus) => t.taskGuid === task?.taskGuid
    ) || task;

  const challengeTask = campaign?.tasks?.find(
    (t) => t.id === currentTask?.taskGuid
  );
  const maxPoints = challengeTask ? getMaxPoints(challengeTask) : 0;

  const modalRef = useRef<HTMLDivElement>(null);

  // Scroll focused input into view when keyboard opens on mobile
  const handleFocusIn = useCallback((e: FocusEvent) => {
    const target = e.target as HTMLElement;
    if (
      target.tagName === "INPUT" ||
      target.tagName === "TEXTAREA" ||
      target.isContentEditable
    ) {
      // Delay to allow the mobile keyboard to finish opening
      setTimeout(() => {
        target.scrollIntoView({ behavior: "smooth", block: "center" });
      }, 300);
    }
  }, []);

  // Close modal on escape key
  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        onClose();
      }
    };

    if (isOpen) {
      document.addEventListener("keydown", handleEscape);
      document.body.style.overflow = "hidden";
    }

    return () => {
      document.removeEventListener("keydown", handleEscape);
      document.body.style.overflow = "unset";
    };
  }, [isOpen, onClose]);

  // Listen for focus events inside the modal to handle mobile keyboard
  useEffect(() => {
    const modal = modalRef.current;
    if (!modal || !isOpen) return;

    modal.addEventListener("focusin", handleFocusIn);
    return () => {
      modal.removeEventListener("focusin", handleFocusIn);
    };
  }, [isOpen, handleFocusIn]);

  if (!currentTask || !isOpen) return null;

  return (
    <>
      {/* Overlay */}
      <div
        className="fixed inset-0 bg-black/60 z-50 transition-opacity duration-200 ease-out"
        onClick={onClose}
      />

      {/* Modal */}
      <div className="fixed inset-0 z-50 flex items-start sm:items-center justify-center p-4 pt-8 sm:pt-4 pointer-events-none overflow-y-auto">
        <div
          ref={modalRef}
          className="bg-white w-full max-w-2xl max-h-[85vh] sm:max-h-[90vh] overflow-y-auto pointer-events-auto relative rounded-2xl shadow-xl mb-4"
          onClick={(e) => e.stopPropagation()}
        >
          {/* Close Button */}
          <button
            onClick={onClose}
            className="absolute top-6 right-6 z-10 bg-white/80 hover:bg-white p-2 rounded-full transition-all duration-200 ease-out hover:shadow-md"
          >
            <X className="w-6 h-6 text-swapper-secondary" />
          </button>

          {/* Header Banner with Quest Image */}
          <div className="relative p-6">
            <div className="relative h-48 overflow-hidden rounded-xl">
              {currentTask.media?.lightModeImageUrl ? (
                <img
                  src={currentTask.media.lightModeImageUrl}
                  alt={currentTask.title}
                  className="absolute inset-0 w-full h-full object-cover"
                />
              ) : (
                <div className="absolute inset-0 w-full h-full bg-gradient-to-br from-swapper-primary to-swapper-accent flex items-center justify-center">
                  <QuestLogo size={64} />
                </div>
              )}

              {/* Points Badge */}
              <div className="absolute bottom-3 right-3 bg-white/90 backdrop-blur-sm text-swapper-primary px-4 py-2 rounded-xl font-bold flex items-center gap-2 shadow-lg">
                {maxPoints}
                <QuestLogo size={16} className="rounded" />
              </div>
            </div>
          </div>

          {/* Content */}
          <div className="px-6 pb-6">
            {/* Title */}
            <h2 className="text-2xl font-bold text-swapper-secondary mb-4">
              {currentTask.title}
            </h2>

            {/* Description */}
            {currentTask.description && (
              <div className="text-sm text-swapper-secondary/70 mb-6 leading-relaxed">
                <MarkdownRenderer>{currentTask.description}</MarkdownRenderer>
              </div>
            )}

            {/* Task Renderer */}
            <div>
              {campaign && (
                <QuestRenderer
                  task={currentTask}
                  campaign={campaign}
                  address={address || ""}
                  challengesUser={challengesUser}
                  completeTaskMutation={taskMutation}
                />
              )}
            </div>
          </div>
        </div>
      </div>
    </>
  );
};
